<template>
  <PopDialog2
    title="방 정보 수정"
    :visible="props.visible"
    :okCallback="onOkClick"
    :cancelCallback="onCancelClick"
  >
    <div class="md-layout">
      <div class="md-layout-item md-size-50 mx-auto text-center mb-4">
        <h2 class="title mt-4 mb-2">{{ isCoolingMode ? '에어컨 패턴' : '보일러 패턴' }}</h2>
        <p v-if="isCoolingMode" class="text-muted mb-3 aircon-pattern-hint">
          ON: 설정온도 · OFF: 설정온도+2℃ (전원 유지)
        </p>

        <hr class="mb-3" />

        <template v-for="pattern in fixedPatterns" :key="pattern.id">
          <div class="text-start mb-2" style="font-size: 1rem">{{ pattern.id }}</div>
          <div class="progress mb-1">
            <div
              class="progress-bar"
              :class="isCoolingMode ? 'bg-info' : 'bg-warning'"
              role="progressbar"
              :style="`width: ${pattern.onPercent}%`"
            >
              {{ pattern.onLabel }}
            </div>
            <div
              class="progress-bar bg-secondary"
              role="progressbar"
              :style="`width: ${pattern.offPercent}%`"
            >
              {{ pattern.offLabel }}
            </div>
          </div>
          <hr class="mb-3" />
        </template>

        <div class="row mb-2">
          <div class="col-6 text-start" style="font-size: 1rem">P5</div>
          <div
            class="col text-end"
            :class="currentPattern == 5 ? 'text-muted' : 'text-muted'"
            :style="`font-size: 0.8rem; cursor: ${currentPattern == 5 ? 'pointer' : 'not-allowed'}; opacity: ${currentPattern == 5 ? '1' : '0.2'};`"
            @click="currentPattern == 5 ? onP5ExpandClick() : null"
          >
            설정&nbsp;&nbsp;
            <i class="fa fa-chevron-down" style="font-size: 0.8rem" v-if="isP5Expanded" />
            <i class="fa fa-chevron-right" style="font-size: 0.8rem" v-else />
          </div>
        </div>
        <div class="progress mb-3">
          <div
            class="progress-bar"
            :class="isCoolingMode ? 'bg-info' : 'bg-warning'"
            role="progressbar"
            :style="`width: ${startTimePercent}%`"
          >
            {{ startTime }}분 {{ onCycleWord }}
          </div>
          <div
            class="progress-bar bg-secondary"
            role="progressbar"
            :style="`width: ${endTimePercent}%`"
          >
            {{ endTime }}분 {{ offCycleWord }}
          </div>
        </div>
        <div class="card" v-if="isP5Expanded">
          <div class="card-body">
            <Slider
              :name="isCoolingMode ? 'ON 시간' : '켜지는 시간'"
              id="start"
              :default="startTime"
              :nameWidth="3"
              :min="p5OnMin"
              :max="p5OnMax"
              :step="5"
              value-suffix="분"
              @onSliderChanged="onStartTimeChanged"
            />
            <Slider
              :name="isCoolingMode ? 'OFF 시간' : '꺼지는 시간'"
              id="end"
              :default="endTime"
              :nameWidth="3"
              :min="p5OffMin"
              :max="p5OffMax"
              :step="5"
              value-suffix="분"
              @onSliderChanged="onEndTimeChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </PopDialog2>
</template>

<script setup>
import { ref, defineProps, computed, getCurrentInstance, reactive } from 'vue';
import { useStore } from 'vuex';
import { ButtonConfig, MQTTTempCommand } from '@/common/constants';
import useMqttControl from '@/common/hooks/useMqttControl';
import PopDialog2 from '@/common/components/popup/PopDialog2.vue';
import Slider from '@/common/components/widgets/Slider.vue';

/** 보일러 고정 패턴 (켜짐/꺼짐 = 전원 사이클) */
const BOILER_FIXED_PATTERNS = [
  { id: 'P1', on: 10, off: 20 },
  { id: 'P2', on: 10, off: 30 },
  { id: 'P3', on: 10, off: 40 },
  { id: 'P4', on: 10, off: 50 },
];

/** 에어컨 고정 패턴 (ON/OFF = 설정온도/설정+2℃, 전원 유지) — xsns_83_neopool */
const AIRCON_FIXED_PATTERNS = [
  { id: 'P1', on: 30, off: 10 },
  { id: 'P2', on: 40, off: 10 },
  { id: 'P3', on: 50, off: 15 },
  { id: 'P4', on: 60, off: 15 },
];

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const store = useStore();
const props = defineProps([
  'visible',
  'okCallback',
  'cancelCallback',
  'roomName',
  'roomId',
  'handlePowerClick',
  'isCoolingMode',
]);

const zone = computed(() => {
  if (store.state.home.zones.length > 0) {
    return store.getters['home/getCurrentZone'];
  }
  return null;
});
const group = computed(() => {
  return zone.value.groups.find((g) => g._id === props.roomId);
});
const devices = computed(() => {
  return group.value ? group.value.devices : undefined;
});
const boilerDevices = computed(() => {
  return devices.value.filter((d) => d.type === 2);
});

const isCoolingMode = computed(() => {
  if (props.isCoolingMode != null) {
    return !!props.isCoolingMode;
  }
  return boilerDevices.value.length > 0 && boilerDevices.value[0].item?.hcMode === 1;
});

const onCycleWord = computed(() => (isCoolingMode.value ? 'ON' : '켜짐'));
const offCycleWord = computed(() => (isCoolingMode.value ? 'OFF' : '꺼짐'));

const fixedPatterns = computed(() => {
  const source = isCoolingMode.value ? AIRCON_FIXED_PATTERNS : BOILER_FIXED_PATTERNS;
  return source.map((p) => {
    const total = p.on + p.off;
    return {
      id: p.id,
      onPercent: (p.on / total) * 100,
      offPercent: (p.off / total) * 100,
      onLabel: `${p.on}분 ${onCycleWord.value}`,
      offLabel: `${p.off}분 ${offCycleWord.value}`,
    };
  });
});

const currentPattern = ref(
  boilerDevices.value.length > 0 ? boilerDevices.value[0].item.pattern : '0',
);
const isP5Expanded = ref(false);

const defaultP5On = () => {
  const customOn = boilerDevices.value[0]?.item?.customPattern?.on;
  if (customOn != null) return customOn;
  return isCoolingMode.value ? 30 : 10;
};
const defaultP5Off = () => {
  const customOff = boilerDevices.value[0]?.item?.customPattern?.off;
  if (customOff != null) return customOff;
  return isCoolingMode.value ? 10 : 30;
};

const startTime = ref(boilerDevices.value.length > 0 ? defaultP5On() : 10);
const endTime = ref(boilerDevices.value.length > 0 ? defaultP5Off() : 10);

const p5OnMin = computed(() => (isCoolingMode.value ? 10 : 5));
const p5OnMax = computed(() => (isCoolingMode.value ? 90 : 30));
const p5OffMin = computed(() => (isCoolingMode.value ? 5 : 10));
const p5OffMax = computed(() => (isCoolingMode.value ? 30 : 60));

const startTimePercent = computed(() => {
  const startValue = parseInt(startTime.value, 10);
  const endValue = parseInt(endTime.value, 10);
  return (startValue / (startValue + endValue)) * 100;
});
const endTimePercent = computed(() => {
  const startValue = parseInt(startTime.value, 10);
  const endValue = parseInt(endTime.value, 10);
  return (endValue / (startValue + endValue)) * 100;
});

const onCancelClick = () => {
  if (props.cancelCallback) {
    props.cancelCallback();
  }
};

const onOkClick = () => {
  if (props.okCallback) {
    props.okCallback();
  } else if (props.cancelCallback) {
    props.cancelCallback();
  }
};

const onP5ExpandClick = () => {
  isP5Expanded.value = !isP5Expanded.value;
};

const onCycleCommand = computed(() =>
  isCoolingMode.value ? MQTTTempCommand.AirOnCycleSet : MQTTTempCommand.TimeOnCycleSet,
);
const offCycleCommand = computed(() =>
  isCoolingMode.value ? MQTTTempCommand.AirOffCycleSet : MQTTTempCommand.TimeOffCycleSet,
);

const onStartTimeChanged = (e) => {
  startTime.value = e;
  emitter.emit('showLoading');
  try {
    boilerDevices.value.forEach((device) => {
      mqttControl.onPublishTemp(device.controllerId, onCycleCommand.value, e);
    });
    setTimeout(
      () => emitter.emit('hideLoading'),
      boilerDevices.value.length > 1 ? ButtonConfig.delayBatch : ButtonConfig.delay,
    );
  } catch (err) {
    emitter.emit('hideLoading');
  }
};

const onEndTimeChanged = (e) => {
  endTime.value = e;
  emitter.emit('showLoading');
  try {
    boilerDevices.value.forEach((device) => {
      mqttControl.onPublishTemp(device.controllerId, offCycleCommand.value, e);
    });
    setTimeout(
      () => emitter.emit('hideLoading'),
      boilerDevices.value.length > 1 ? ButtonConfig.delayBatch : ButtonConfig.delay,
    );
  } catch (err) {
    emitter.emit('hideLoading');
  }
};
</script>

<style scoped>
.aircon-pattern-hint {
  font-size: 0.8rem;
  line-height: 1.35;
}
</style>
